import { useState, useMemo } from "react";
import { ChevronLeft, ShoppingCart, Package, Car, Cpu, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice, BRAND_META, BRAND_COLORS, BRAND_LOGOS, BRAND_BG } from "./shopData";

export default function ProductDetail({ product, onBack, onAdd, onEngineCodeClick, onMakeClick, onToggleWishlist, inWishlist }) {
  const [imgFailed, setImgFailed] = useState(false);
  const meta = BRAND_META[product.brand] || {};
  const brandColor = BRAND_COLORS[product.brand] || { bg: "#1a1a1a", text: "#FF3B30" };

  // Groepeer car_applications per merk voor de compatibiliteitslijst.
  // De data komt 1-op-1 van de leverancier — we dedupliceren alleen
  // gelijke (make, model) paren om ruis te vermijden.
  const compatByMake = useMemo(() => {
    const map = new Map();
    (product.car_applications || []).forEach((app) => {
      const make = (app.make || "").trim();
      const model = (app.model || "").trim();
      if (!make) return;
      const key = make.toLowerCase();
      if (!map.has(key)) map.set(key, { make, models: new Set() });
      if (model) map.get(key).models.add(model);
    });
    // Als er GEEN car_applications maar wél een primair car_make is: gebruik dat als fallback
    if (map.size === 0 && product.car_make) {
      const key = product.car_make.toLowerCase();
      map.set(key, { make: product.car_make, models: new Set(product.car_model ? [product.car_model] : []) });
    }
    return Array.from(map.values())
      .map((g) => ({ make: g.make, models: Array.from(g.models).sort((a, b) => a.localeCompare(b, "nl")) }))
      .sort((a, b) => a.make.localeCompare(b.make, "nl"));
  }, [product.car_applications, product.car_make, product.car_model]);

  const engineCodes = useMemo(() => {
    const set = new Set();
    (product.engine_codes || []).forEach((c) => {
      const t = String(c || "").trim();
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [product.engine_codes]);

  const hasCompat = compatByMake.length > 0 || engineCodes.length > 0;

  return (
    <div data-testid="product-detail" className="max-w-4xl mx-auto pt-4">
      <button data-testid="product-detail-back" onClick={onBack} className="flex items-center gap-1.5 text-[#666] hover:text-white text-sm mb-6 transition-colors">
        <ChevronLeft size={16} /> Terug
      </button>
      <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          <div className="bg-white flex items-center justify-center min-h-[350px] relative">
            {product.image_url && !imgFailed ? (
              <img src={product.image_url} alt={product.title} className="w-full h-full object-contain p-6 max-h-[500px]" onError={() => setImgFailed(true)} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-8" style={{ background: brandColor.bg }}>
                {BRAND_LOGOS[product.brand] ? (
                  <img src={BRAND_LOGOS[product.brand]} alt={product.brand} className="w-32 h-32 object-contain opacity-70" />
                ) : (
                  <span className="font-['Orbitron'] font-bold text-2xl text-center" style={{ color: brandColor.text }}>{product.brand}</span>
                )}
                <span className="text-sm text-[#555] mt-3">{product.manufacturer_item}</span>
              </div>
            )}
            {product.brand && (
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <span className="bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">{product.brand}</span>
                {product.product_category && <span className="bg-black/60 text-white/80 text-[10px] px-2 py-0.5 rounded">{product.product_category}</span>}
              </div>
            )}
          </div>
          <div className="p-6 sm:p-8 flex flex-col">
            <div className="flex-1">
              <h1 className="text-white text-xl sm:text-2xl font-bold leading-tight mb-4">{product.title}</h1>
              {meta.tagline && <p className="text-[#FF3B30] text-xs uppercase tracking-wider font-semibold mb-4">{meta.tagline}</p>}
        {(product.description_nl || product.description) && (
          <div className="mb-6">
            <p className="text-[#999] text-sm leading-relaxed">{product.description_nl || product.description}</p>
          </div>
        )}
              <div className="space-y-2.5 mb-6">
                {product.manufacturer_item && (
                  <div className="flex items-center justify-between text-sm"><span className="text-[#666]">Artikelnummer</span><span className="text-white font-mono text-xs">{product.manufacturer_item}</span></div>
                )}
                {product.product_category && (
                  <div className="flex items-center justify-between text-sm"><span className="text-[#666]">Categorie</span><span className="text-white">{product.product_category}</span></div>
                )}
              </div>
            </div>
            <div className="border-t border-[#1E1E1E] pt-6 space-y-4">
              {product.available !== undefined && product.available !== null && (
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${product.available > 0 ? 'bg-[#27AE60]/10 border border-[#27AE60]/30' : 'bg-[#333]/30 border border-[#333]'}`}>
                  <span className={`w-2 h-2 rounded-full ${product.available > 0 ? 'bg-[#27AE60]' : 'bg-[#666]'}`} />
                  <span className={product.available > 0 ? 'text-[#27AE60]' : 'text-[#888]'}>
                    {product.available > 0 ? `${product.available} op voorraad` : "Op bestelling (3-7 werkdagen)"}
                  </span>
                </div>
              )}
              <div className="flex items-end justify-between">
                <div><span className="text-[#666] text-xs block mb-1">Prijs incl. BTW</span><span className="text-[#FF3B30] text-3xl font-bold">{formatPrice(product.price)}</span></div>
              </div>
              <p className="text-[#555] text-[10px]">Prijzen zijn onder voorbehoud en kunnen afwijken door wisselende leverancierstarieven.</p>
              <Button data-testid="product-detail-add-cart" onClick={() => onAdd(product)}
                className="w-full bg-[#FF3B30] hover:bg-[#E0342A] text-white py-5 font-bold uppercase tracking-wider rounded-lg transition-all hover:shadow-[0_0_20px_rgba(255,59,48,0.3)]">
                <ShoppingCart size={18} className="mr-2" /> In winkelwagen
              </Button>
              {onToggleWishlist && (
                <button
                  onClick={() => onToggleWishlist(product)}
                  data-testid="product-detail-wishlist"
                  className={`w-full mt-2 py-3 rounded-lg text-sm font-semibold border transition-all inline-flex items-center justify-center gap-2 ${inWishlist ? "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF3B30]" : "border-[#1E1E1E] text-[#888] hover:text-white hover:border-[#333]"}`}
                >
                  <Heart size={15} fill={inWishlist ? "#FF3B30" : "none"} />
                  {inWishlist ? "Op verlanglijst" : "Op verlanglijst"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Compatibele voertuigen (onder de foto + info) ── */}
        {hasCompat && (
          <div className="border-t border-[#1E1E1E] p-6 sm:p-8" data-testid="compat-section">
            <div className="flex items-center gap-2 mb-4">
              <Car size={17} className="text-[#FF3B30]" />
              <h2 className="text-white font-bold text-lg">Compatibele voertuigen</h2>
              <span className="ml-auto text-[#555] text-[11px]">
                {compatByMake.length} merk{compatByMake.length !== 1 ? "en" : ""} ·{" "}
                {compatByMake.reduce((n, g) => n + g.models.length, 0)} model{compatByMake.reduce((n, g) => n + g.models.length, 0) !== 1 ? "len" : ""}
              </span>
            </div>

            {compatByMake.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="compat-makes">
                {compatByMake.map((group) => (
                  <div key={group.make} className="bg-[#0f0f0f] border border-[#1E1E1E] rounded-lg p-3.5" data-testid={`compat-make-${group.make}`}>
                    <button
                      type="button"
                      onClick={() => onMakeClick && onMakeClick(group.make)}
                      className="text-[#F1C40F] text-xs uppercase tracking-wider font-bold mb-2 hover:text-[#FF3B30] transition-colors cursor-pointer"
                      data-testid={`compat-make-btn-${group.make}`}
                      title={`Bekijk alle producten voor ${group.make}`}
                    >
                      {group.make} →
                    </button>
                    {group.models.length > 0 ? (
                      <ul className="space-y-0.5">
                        {group.models.map((m) => (
                          <li key={m} className="text-[#ccc] text-[13px] leading-snug">
                            <span className="text-[#555] mr-1.5">•</span>{m}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[#555] text-xs italic">Modelinformatie niet beschikbaar in bron-catalogus</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {engineCodes.length > 0 && (
              <div data-testid="compat-engines">
                <div className="flex items-center gap-2 mb-2.5">
                  <Cpu size={14} className="text-[#FF3B30]" />
                  <p className="text-white font-bold text-sm">Motorcodes ({engineCodes.length})</p>
                  <span className="text-[#555] text-[10px] ml-1">— klik voor alle producten met deze motor</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {engineCodes.map((code) => (
                    <button
                      type="button"
                      key={code}
                      onClick={() => onEngineCodeClick && onEngineCodeClick(code)}
                      className="inline-flex items-center px-2.5 py-1 rounded-md bg-[#1a1a1a] border border-[#2a2a2a] text-[#F1C40F] font-mono text-[11px] font-semibold hover:bg-[#FF3B30] hover:text-white hover:border-[#FF3B30] transition-all cursor-pointer"
                      data-testid={`engine-code-${code}`}
                      title={`Bekijk alle producten met motorcode ${code}`}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-[#444] text-[10px] mt-4 leading-relaxed">
              <Package size={10} className="inline -mt-0.5 mr-0.5" />
              Compatibiliteitsdata komt direct uit de catalogus van de leverancier ({product.brand}).
              Controleer altijd het artikelnummer met de fabrikant voordat je bestelt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

